import Check from '@mui/icons-material/Check';
import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Chip } from '~components/Chip/Chip';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import type { DriverDay } from '~hooks/useDriverDays/models';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { canSetDriverOffDuty } from './canSetDriverOffDuty';
import ApprovalActionsMenu, { ApprovedChip } from './components/ApprovalActionsMenu';

interface DriverDayStatusProps {
  approvable: boolean;
  approved: boolean;
  driverDayId: string;
  driverCompanyId?: string;
  driverId: string;
  driverIsShared?: boolean;
  isOngoingShift?: boolean;
  onDriverDayChange: (driverDay: DriverDay) => void;
  unapprovable: boolean;
}

export default observer(function DriverDayStatus({
  approvable,
  approved,
  driverDayId,
  driverCompanyId,
  driverId,
  driverIsShared,
  isOngoingShift,
  onDriverDayChange,
  unapprovable,
}: DriverDayStatusProps) {
  const theme = useTheme();
  const { userStore, toasterStore, driverDayStore } = useStores();
  const { approveDriverDay, unapproveDriverDay, getDriverDayById } = useDriverDays();
  const { canApproveShift, canEditUser } = userStore.getPermissions();
  const { setDriverStateToOffDuty } = useDrivers();
  const [isLoadingDriverDay, setIsLoadingDriverDay] = useState(false);
  const companyId = getEffectiveUserCompanyId(userStore);
  const canEndShift =
    canEditUser &&
    isOngoingShift &&
    canSetDriverOffDuty({
      driverCompanyId,
      driverIsShared: Boolean(driverIsShared),
      pickedCompanyId: companyId,
    });

  const handleEndShift = () => {
    setIsLoadingDriverDay(true);
    setDriverStateToOffDuty(driverId)
      .then(() => {
        getDriverDayById(driverDayId).then(onDriverDayChange);
        setIsLoadingDriverDay(false);
      })
      .catch((e) => {
        toasterStore.push(alert(e.message, AlertTypes.error));
      });
  };

  if (!canApproveShift) {
    return (
      <Chip
        hideDropdownArrow={true}
        color={approved ? 'success' : 'secondary'}
        label={approved ? 'approved' : 'pending'}
      />
    );
  }

  const handleApprove = () => {
    driverDayStore.setShiftApprovalLoading(driverDayId, true);
    approveDriverDay(companyId, driverDayId)
      .then((approvedDriverDay) => {
        trackApprovalBatch('driver_day', 1);
        onDriverDayChange(approvedDriverDay);
      })
      .finally(() => {
        driverDayStore.setShiftApprovalLoading(driverDayId, false);
      });
  };

  const handleUnapprove = () => {
    if (!unapprovable) return;
    driverDayStore.setShiftApprovalLoading(driverDayId, true);
    unapproveDriverDay(companyId, driverDayId)
      .then(onDriverDayChange)
      .finally(() => {
        driverDayStore.setShiftApprovalLoading(driverDayId, false);
      });
  };

  if (approved) {
    if (unapprovable) {
      return (
        <ApprovalActionsMenu
          approved
          onMenuItemClick={unapprovable ? handleUnapprove : undefined}
          isLoading={driverDayStore.isShiftApprovalLoading(driverDayId)}
        />
      );
    }

    return <ApprovedChip hideDropdownArrow />;
  }

  if (isOngoingShift) {
    return (
      <Button
        color="error"
        disabled={!canEndShift}
        loading={isLoadingDriverDay}
        onClick={handleEndShift}
        variant="contained"
      >
        {t('actions.end_shift')}
      </Button>
    );
  }

  if (approvable) {
    return (
      <Button
        color="success"
        onClick={handleApprove}
        size="small"
        startIcon={<Check />}
        variant="contained"
        sx={{ bgcolor: theme.brandV2.colors.treadGreen }}
        loading={driverDayStore.isShiftApprovalLoading(driverDayId)}
      >
        {t('actions.approve_shift')}
      </Button>
    );
  }

  return null;
});
